import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Plus, Minus, ChevronRight } from 'lucide-react';
import { useForumCategories, useCreateForumThread } from '@/hooks/useApi';
import toast from 'react-hot-toast';

export default function NewThreadPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { data: categories = [] } = useForumCategories();
  const { mutate: createThread, isLoading } = useCreateForumThread();

  const prefilledSlug = searchParams.get('category') ?? '';
  const allCategories = categories as any[];
  // Flatten the category tree (API returns parents with nested children[])
  const flatCategories = [
    ...allCategories,
    ...allCategories.flatMap((c: any) => c.children ?? []),
  ].filter((c: any) => c.is_active);
  const prefilledCat = flatCategories.find((c: any) => c.slug === prefilledSlug);

  const [categoryId, setCategoryId] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [hasPoll, setHasPoll] = useState(false);
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [pollClosesAt, setPollClosesAt] = useState('');

  // Sync categoryId once categories finish loading
  useEffect(() => {
    if (prefilledCat && !categoryId) {
      setCategoryId(prefilledCat.id);
    }
  }, [prefilledCat?.id]);

  const leafCategories = flatCategories;

  const insertSyntax = (syntax: string) => {
    setBody(prev => prev + syntax);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!categoryId) { toast.error('Select a category'); return; }
    if (title.trim().length < 3) { toast.error('Title must be at least 3 characters'); return; }
    if (!body.trim()) { toast.error('Post body is required'); return; }

    const poll = hasPoll && pollQuestion.trim() ? {
      question: pollQuestion.trim(),
      options: pollOptions.filter(o => o.trim()),
      closes_at: pollClosesAt || undefined,
    } : undefined;

    if (poll && poll.options.length < 2) { toast.error('Poll needs at least 2 options'); return; }

    createThread(
      { category_id: categoryId, title: title.trim(), body: body.trim(), poll },
      {
        onSuccess: (thread: any) => {
          toast.success('Thread created!');
          navigate(`/forum/threads/${thread.id}`);
        },
      }
    );
  };

  return (
    <div className="bg-white dark:bg-zinc-900 min-h-screen">
      <div className="bg-white dark:bg-zinc-900 border-b border-gray-200 dark:border-zinc-800">
        <div className="container-custom py-5">
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-zinc-500 mb-1">
            <Link to="/forum" className="hover:text-brand-600 dark:text-brand-400">Forum</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-gray-800 dark:text-zinc-200">New Thread</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-zinc-100">Start a New Thread</h1>
        </div>
      </div>

      <div className="container-custom py-8 max-w-3xl">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Category */}
          <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-5">
            <label className="block text-sm font-semibold text-gray-700 dark:text-zinc-300 mb-2">Category *</label>
            <select
              value={categoryId}
              onChange={e => setCategoryId(e.target.value)}
              className="w-full border border-gray-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-zinc-900"
            >
              <option value="">Select a category…</option>
              {leafCategories.map((c: any) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Title */}
          <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-5">
            <label className="block text-sm font-semibold text-gray-700 dark:text-zinc-300 mb-2">Title *</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="What's on your mind?"
              maxLength={200}
              className="w-full border border-gray-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
            />
            <p className="text-xs text-gray-500 dark:text-zinc-500 mt-1 text-right">{title.length}/200</p>
          </div>

          {/* Body */}
          <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-5">
            <label className="block text-sm font-semibold text-gray-700 dark:text-zinc-300 mb-2">Post *</label>
            <div className="flex items-center gap-1 mb-2 border-b border-gray-200 dark:border-zinc-800 pb-2">
              <button type="button" onClick={() => insertSyntax('**bold**')}
                className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-zinc-800 dark:bg-zinc-800 text-gray-500 dark:text-zinc-500 text-xs font-bold" title="Bold">B</button>
              <button type="button" onClick={() => insertSyntax('_italic_')}
                className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-zinc-800 dark:bg-zinc-800 text-gray-500 dark:text-zinc-500 text-xs italic" title="Italic">I</button>
              <div className="h-4 w-px bg-gray-200 dark:bg-zinc-700 mx-1" />
              <button type="button"
                onClick={() => {
                  const name = window.prompt('Card name:');
                  if (name) insertSyntax(`[[${name}]]`);
                }}
                className="flex items-center gap-1 px-2 py-1 rounded bg-brand-50 dark:bg-brand-900/20 text-brand-600 dark:text-brand-400 hover:bg-brand-100 dark:bg-brand-900/40 text-xs font-medium">
                [[Card]]
              </button>
            </div>
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              placeholder="Share your thoughts... Use [[Card Name]] to mention a card."
              rows={8}
              className="w-full border border-gray-200 dark:border-zinc-800 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none"
            />
          </div>

          {/* Poll toggle */}
          <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-5">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={hasPoll}
                onChange={e => setHasPoll(e.target.checked)}
                className="accent-brand-600 h-4 w-4"
              />
              <span className="text-sm font-semibold text-gray-700 dark:text-zinc-300">Add a poll</span>
            </label>

            {hasPoll && (
              <div className="mt-4 space-y-4">
                <div>
                  <label className="block text-sm text-gray-600 dark:text-zinc-400 mb-1">Poll question</label>
                  <input
                    type="text"
                    value={pollQuestion}
                    onChange={e => setPollQuestion(e.target.value)}
                    placeholder="Ask something…"
                    className="w-full border border-gray-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 dark:text-zinc-400 mb-2">Options (min 2, max 6)</label>
                  <div className="space-y-2">
                    {pollOptions.map((opt, i) => (
                      <div key={i} className="flex gap-2">
                        <input
                          type="text"
                          value={opt}
                          onChange={e => {
                            const next = [...pollOptions];
                            next[i] = e.target.value;
                            setPollOptions(next);
                          }}
                          placeholder={`Option ${i + 1}`}
                          className="flex-1 border border-gray-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                        />
                        {pollOptions.length > 2 && (
                          <button type="button"
                            onClick={() => setPollOptions(pollOptions.filter((_, j) => j !== i))}
                            className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 dark:bg-red-900/20 rounded-lg">
                            <Minus className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  {pollOptions.length < 6 && (
                    <button type="button"
                      onClick={() => setPollOptions([...pollOptions, ''])}
                      className="flex items-center gap-1 text-sm text-brand-600 dark:text-brand-400 hover:underline mt-2">
                      <Plus className="h-3.5 w-3.5" /> Add option
                    </button>
                  )}
                </div>
                <div>
                  <label className="block text-sm text-gray-600 dark:text-zinc-400 mb-1">Close date (optional)</label>
                  <input
                    type="datetime-local"
                    value={pollClosesAt}
                    onChange={e => setPollClosesAt(e.target.value)}
                    className="border border-gray-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between">
            <Link to="/forum" className="text-sm text-gray-500 dark:text-zinc-500 hover:text-gray-800 dark:text-zinc-200">Cancel</Link>
            <button type="submit" disabled={isLoading}
              className="btn-primary py-2.5 px-8 disabled:opacity-50">
              {isLoading ? 'Posting…' : 'Post Thread'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
